import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import { styled } from 'baseui';
import { SnackbarProvider } from 'baseui/snackbar';
import { PLACEMENT, ToasterContainer } from 'baseui/toast';
import type { Atom, createStore } from 'jotai';
import type { Logger } from '../..';
import { AnalyticsProvider } from '../../analytics/analytics-provider';
import type { SendEventT } from '../../analytics/types';
import BrowserRecommendationBanner from '../../common/components/browser-recommendation-banner';
import { APIContextProvider } from '../../common/providers/api-provider';
import type { GetULabelConfigFunction } from '../../common/providers/feature-flag-provider';
import { type GetFeatureFlagFunctionT, FeatureFlagProvider } from '../../common/providers/feature-flag-provider';
import { ModalWrapperProvider } from '../../common/providers/modal-wrapper-provider';
import { StorageProvider } from '../../common/providers/storage-provider';
import type { ReviewOutputDataT } from '../../common/review-mode-side-panel';
import { ReviewHeaderActions, ReviewOutputObserver } from '../../common/review-mode-side-panel';
import { SidePanelContainer, SidePanelContainerM4 } from '../../common/side-panel/styled-components';
import { TaskStateProvider } from '../../common/task-state/providers/task-state-provider';
import { ThemeSyncer } from '../../common/theme/theme-syncer';
import type { AnnotationLabelT, ConfigT, GetSignedUrlT, ShowNotificationT, TaskLevelQuestionAnswersT } from '../../common/types/types';
import type { RegisterHotkeysArgsT, RegisterHotkeysResultT } from '../../common/types/types';
import { type TaskStateT } from '../../common/types/types';
import { MetricsHydrator } from '../../metrics/metrics-hydrator';
import type { MetricsTrackerT } from '../../metrics/types';
import { TaskLevelQuestionnaireProvider } from '../../modernization-common/provider/task-level-questionnaire';
import type { RelationshipLabelDefinitionT } from '../../modernization-common/relationships';
import type { ImageQAEngine } from '../../qa-checks/type/qa-engine';
import type { WebWorker } from '../../workers/types/common';
import { PDFProvider } from '../labelling/providers/pdf-provider';
import { TutorialProvider } from '../labelling/providers/tutorial-provider';
import { DEFAULT_STORE } from '../labelling/store';
import type { ImageQuadrantValidationConfig, InputDataT, onStoreChangeT, SuperpixelImageRegionsDataT, TutorialProviderT } from '../labelling/types';
import { getFramesFromInputData, initializeTaskState } from '../labelling/utils';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, Sam3TextPromptRequestT, SamModelImagePluginRequestT, SamModelPluginRequestT, SAMSegmentImageData, SAMSegmentVideoData, StudioRef, TrackObjectsOutputT, TrackObjectsRequestT } from '../types';
import StudioAtomHydrator from '../utils/atom-hydrator';
import { readOnlyAtom, taskModeAtom } from './atoms/task-mode-atoms';
import { AccurateFrameRenderingFlagSync } from './components/accurate-frame-rendering-flag-sync';
import type { EditorContainerRef } from './components/editor/container';
import EditorContainer from './components/editor/container';
import type { ThemeOptions } from './components/labeling-settings/labeling-settings';
import { LabelingSettings } from './components/labeling-settings/labeling-settings';
import { LabelingTaskHeader } from './components/labeling-task-header/labeling-task-header';
import { OutputObserver } from './components/output-observer/output-observer';
import { PerformanceInitializer } from './components/performance-initializer';
import { ImageSidePanel } from './components/side-panel/side-panel';
import { ImageSidePanel as ImageSidePanelM4 } from './components/side-panel-m4/side-panel';
import { SidePanelOverlay } from './components/side-panel-overlay';
import StageControlToolBar from './components/stage-control-toolbar/stage-control-toolbar';
import { TaskQuestionnaireSync } from './components/task-questionnaire-sync/task-questionnaire-sync';
import { TrackingActiveObserver } from './components/tracking-active-observer/tracking-active-observer';
import type { WindowedHandles } from './components/windowed-handles-initializer';
import { WINDOWED_HANDLES_DEFAULTS, WindowedHandlesInitializer } from './components/windowed-handles-initializer';
import { BufferedEventJournalerInitializer } from './event-journal/components/buffered-event-journaler-initializer';
import { EventJournalConsumerInitializer } from './event-journal/components/event-journal-consumer-initializer';
import { PersistenceOrchestratorInitializer } from './event-journal/components/persistence-orchestrator-initializer';
import { StudioStoreInitializer } from './studio-store/components/studio-store-initializer';
import type { StudioConfigT } from './studio-store/types';
import { LABELING_TITLES } from './constants';
const Container = styled('div', () => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));
const Main = styled('div', ({
  $theme
}) => ({
  width: '100%',
  height: `calc(100% - ${$theme.sizing.scale1400})`,
  display: 'flex'
}));
type PropsT = {
  headerActionsElement?: React.ReactNode;
  inputData: InputDataT;
  annotationLabels: AnnotationLabelT[];
  onStoreChange?: onStoreChangeT;
  onReviewStateChange?: (reviewOutput: ReviewOutputDataT) => void;
  onTrackingActiveChange?: (isActive: boolean) => void;
  showNotification?: ShowNotificationT;
  fetchQuestionnaire: (questionnaireId: string) => Promise<QnAStateType>;
  sendEvent?: SendEventT;
  setItemInStorage: (key: string, value: string, isPersistent: boolean) => void;
  getItemFromStorage: (key: string, isPersistent: boolean) => string;
  tutorialProvider?: TutorialProviderT;
  getSuperpixelImageRegionsData?: () => Promise<SuperpixelImageRegionsDataT>;
  isFeatureEnabled: GetFeatureFlagFunctionT;
  renderPDFFrame?: (currentPage: number, canvasContext: CanvasRenderingContext2D) => Promise<void>;
  getPdfPage?: (currentPage: number) => Promise<{
    view: [number, number, number, number];
  }>;
  config?: ConfigT;
  getSAMImageDataPoints?: (input: SamModelImagePluginRequestT) => Promise<SAMSegmentImageData[]>;
  getSAM3ImageDataPoints?: (input: Sam3TextPromptRequestT) => Promise<SAMSegmentImageData[]>;
  getSamSegmentData?: (input: SamModelPluginRequestT, labelTitle?: string) => Promise<SAMSegmentImageData | Error | null>;
  sam3Config?: {
    isSAM3Enabled: boolean;
    isSAM3PolygonToolsEnabled: boolean;
    isSAM3PolygonTightnessSliderEnabled: boolean;
  };
  getAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  enableStageToolBar?: boolean;
  enableUndoRedoStageToolBar?: boolean;
  classificationQuestionnaire?: {
    Component: ({
      onClose
    }: {
      onClose?: () => void;
    }) => React.ReactNode;
    status: 'PENDING' | 'DONE' | 'OPTIONAL';
  };
  errorBoundaryComponent?: React.ComponentType<{
    children: React.ReactNode;
  }>;
  metricsTracker?: MetricsTrackerT;
  fetchFileSizeAndFormat?: (src: string) => Promise<{
    fileSize: number | null;
    format: string | null;
  }>;
  getSignedUrl?: GetSignedUrlT;
  getULabelConfig?: GetULabelConfigFunction;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
  getBoundingBoxData?: (input: BoundingBoxTrackingInputT) => Promise<BoundingBoxTrackingOutputT | Error>;
  trackObjectsAcrossKeyframes?: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
  registerHotKeys?: (args: RegisterHotkeysArgsT) => RegisterHotkeysResultT;
  webWorkers?: {
    videoFrameProcessor?: WebWorker;
  };
  logger?: Logger;
  onEditorReady?: (ref: StudioRef) => void;
  relationshipLabels?: RelationshipLabelDefinitionT[];
  qaEngine?: ImageQAEngine;
  /** Enabled via account feature `image_quadrant_validation_remaining_items` (labeling-web). */
  imageQuadrantValidationConfig?: ImageQuadrantValidationConfig;
  studioConfig?: StudioConfigT;
  taskLevelQuestionnaireResultAtom?: {
    atom: Atom<TaskLevelQuestionAnswersT>;
    store?: ReturnType<typeof createStore>;
  };
  isVideoLabelingM4Enabled?: boolean;
  headerDetails?: {
    appName: string;
    userName: string;
  };
  onThemeChange?: (theme: ThemeOptions) => void;
  enableKeyframeStepperTracking?: boolean;
};
const Studio = forwardRef<StudioRef, PropsT>(function Studio({
  headerActionsElement,
  inputData,
  annotationLabels,
  onStoreChange,
  onReviewStateChange,
  onTrackingActiveChange,
  sendEvent,
  setItemInStorage,
  getItemFromStorage,
  tutorialProvider,
  getSuperpixelImageRegionsData,
  isFeatureEnabled,
  renderPDFFrame,
  getPdfPage,
  getSAMImageDataPoints,
  getSAM3ImageDataPoints,
  getSamSegmentData,
  getAnnotationTrackingData,
  enableStageToolBar,
  enableUndoRedoStageToolBar,
  classificationQuestionnaire,
  fetchQuestionnaire,
  errorBoundaryComponent,
  metricsTracker,
  fetchFileSizeAndFormat,
  getSignedUrl,
  onValidateAnswers,
  getBoundingBoxData,
  trackObjectsAcrossKeyframes,
  registerHotKeys,
  getULabelConfig,
  config,
  webWorkers,
  logger,
  onEditorReady,
  qaEngine,
  imageQuadrantValidationConfig,
  relationshipLabels,
  studioConfig,
  taskLevelQuestionnaireResultAtom,
  sam3Config,
  isVideoLabelingM4Enabled = false,
  headerDetails,
  onThemeChange,
  enableKeyframeStepperTracking = false
}, ref) {
  const editorContainerRef = useRef<EditorContainerRef>(null);
  const windowedHandlesRef = useRef<WindowedHandles>(null);
  const {
    videoFrameProcessor
  } = webWorkers ?? {};
  const studioRefValue = useMemo<StudioRef>(() => ({
    selectAnnotation: (frameIndex: number, annotationId: string) => {
      editorContainerRef.current?.selectAnnotation(frameIndex, annotationId);
    },
    drainJournal: () => (windowedHandlesRef.current ?? WINDOWED_HANDLES_DEFAULTS).drainJournal(),
    getPendingJournalCount: () => (windowedHandlesRef.current ?? WINDOWED_HANDLES_DEFAULTS).getPendingJournalCount(),
    getWindowedSubmitData: () => (windowedHandlesRef.current ?? WINDOWED_HANDLES_DEFAULTS).getWindowedSubmitData(),
    getValidations: () => (windowedHandlesRef.current ?? WINDOWED_HANDLES_DEFAULTS).getValidations()
  }), []);
  useImperativeHandle(ref, () => studioRefValue);
  useEffect(() => {
    onEditorReady?.(studioRefValue);
  }, [onEditorReady, studioRefValue]);
  const initialState = getFramesFromInputData(inputData, annotationLabels);
  const relationshipStore = useMemo(() => {
    return Object.fromEntries(inputData.relationships?.map(relationship => [relationship.id, relationship]) ?? []);
  }, [inputData.relationships]);
  const taskState: TaskStateT = useMemo(() => initializeTaskState(initialState, inputData.taskState), [initialState, inputData.taskState]);
  if (!initialState) {
    return <>No Data</>;
  }
  const resolvedInitialState = {
    ...DEFAULT_STORE,
    ...initialState,
    ...taskState,
    relationshipStore,
    relationshipLabels,
    sam3Config,
    isVideoLabelingM4Enabled,
    enableKeyframeStepperTracking
  };

  // When caller doesn't provide studioConfig, default to memory mode with the local initialState.
  // Callers using windowed mode pass { mode: 'windowed', taskKey } explicitly.
  const resolvedStudioConfig: StudioConfigT = studioConfig ?? {
    mode: 'memory',
    initialState: resolvedInitialState
  };
  function getLabelingTitle(): string {
    if ('imageUrl' in inputData) {
      return LABELING_TITLES.IMAGE;
    } else if ('videoUrl' in inputData) {
      return LABELING_TITLES.VIDEO;
    } else if ('pdfUrl' in inputData) {
      return LABELING_TITLES.PDF;
    }
    return LABELING_TITLES.DEFAULT;
  }

  // IF no error boundary component is provided, use React.Fragment
  // Which is like a empty component that does nothing
  const ErrorBoundary = errorBoundaryComponent || React.Fragment;
  return <>
      <BrowserRecommendationBanner />
      <TaskStateProvider taskState={taskState}>
        <StorageProvider setItemInStorage={setItemInStorage} getItemFromStorage={getItemFromStorage}>
          <APIContextProvider getSignedUrl={getSignedUrl} getAnnotationTrackingData={getAnnotationTrackingData} getSamSegmentData={getSamSegmentData} getSAMImageDataPoints={getSAMImageDataPoints} getSAM3ImageDataPoints={getSAM3ImageDataPoints} fetchQuestionnaire={fetchQuestionnaire} fetchFileSizeAndFormat={fetchFileSizeAndFormat} getBoundingBoxData={getBoundingBoxData} trackObjectsAcrossKeyframes={trackObjectsAcrossKeyframes} registerHotKeys={registerHotKeys} videoFrameProcessor={videoFrameProcessor}>
            <FeatureFlagProvider getFeatureFlag={isFeatureEnabled}>
              <StudioAtomHydrator initialState={resolvedInitialState} logger={logger} qaEngine={qaEngine} inputData={inputData} imageQuadrantValidationConfig={imageQuadrantValidationConfig} taskState={taskState} studioConfig={resolvedStudioConfig}>
                <ThemeSyncer />
                <WindowedHandlesInitializer ref={windowedHandlesRef} />
                <StudioStoreInitializer studioConfig={resolvedStudioConfig}>
                  <BufferedEventJournalerInitializer>
                    <PersistenceOrchestratorInitializer>
                      <EventJournalConsumerInitializer>
                        <TaskLevelQuestionnaireProvider taskLevelQuestionnaireAtom={taskLevelQuestionnaireResultAtom}>
                          <TaskQuestionnaireSync />
                          <AccurateFrameRenderingFlagSync getFeatureFlag={isFeatureEnabled} />
                          <MetricsHydrator metricsTracker={metricsTracker}>
                            <AnalyticsProvider sendEvent={sendEvent}>
                              <TutorialProvider tutorialProvider={tutorialProvider}>
                                <SnackbarProvider>
                                  <PerformanceInitializer />
                                  <Container>
                                    <ModalWrapperProvider>
                                      <LabelingTaskHeader pageName={headerDetails?.appName} title={getLabelingTitle()} controls={<StageControlToolBar />} pageControls={headerDetails && onThemeChange ? <LabelingSettings userName={headerDetails.userName} onThemeChange={onThemeChange} /> : undefined} reviewControls={taskState.reviewModeVersion === 2 ? <ReviewHeaderActions taskModeAtom={taskModeAtom} readOnlyAtom={readOnlyAtom} /> : undefined}>
                                        {headerActionsElement}
                                      </LabelingTaskHeader>
                                      <Main>
                                        <ErrorBoundary>
                                          {renderPDFFrame && getPdfPage ? <PDFProvider renderPDFFrame={renderPDFFrame} getPage={getPdfPage}>
                                              <EditorContainer getULabelConfig={getULabelConfig} enableUndoRedoStageToolBar={enableUndoRedoStageToolBar} enableStageToolBar={enableStageToolBar} showPredefinedSAMToolSizes={false} getSuperpixelImageRegionsData={getSuperpixelImageRegionsData} frameRate={'frameRate' in inputData ? inputData.frameRate : undefined} ref={editorContainerRef} />
                                            </PDFProvider> : <EditorContainer getULabelConfig={getULabelConfig} enableUndoRedoStageToolBar={enableUndoRedoStageToolBar} enableStageToolBar={enableStageToolBar} showPredefinedSAMToolSizes={false} getSuperpixelImageRegionsData={getSuperpixelImageRegionsData} frameRate={'frameRate' in inputData ? inputData.frameRate : undefined} ref={editorContainerRef} />}
                                          {isVideoLabelingM4Enabled ? <SidePanelContainerM4 $customWidth="335px">
                                              <SidePanelOverlay>
                                                <ImageSidePanelM4 classificationQuestionnaire={classificationQuestionnaire} labelingTitle={getLabelingTitle()} onValidateAnswers={onValidateAnswers} />
                                              </SidePanelOverlay>
                                            </SidePanelContainerM4> : <SidePanelContainer $customWidth="335px">
                                              <SidePanelOverlay>
                                                <ImageSidePanel classificationQuestionnaire={classificationQuestionnaire} labelingTitle={getLabelingTitle()} onValidateAnswers={onValidateAnswers} config={config} />
                                              </SidePanelOverlay>
                                            </SidePanelContainer>}
                                          {onReviewStateChange && taskState.reviewModeVersion === 2 && <ReviewOutputObserver onReviewStateChange={onReviewStateChange} reviewOutput={inputData.feedbackData} />}
                                          {onStoreChange && <OutputObserver onStoreChange={onStoreChange} />}
                                          {onTrackingActiveChange && <TrackingActiveObserver onTrackingActiveChange={onTrackingActiveChange} />}
                                        </ErrorBoundary>
                                      </Main>
                                    </ModalWrapperProvider>
                                  </Container>
                                </SnackbarProvider>
                              </TutorialProvider>
                            </AnalyticsProvider>
                          </MetricsHydrator>

                          <ToasterContainer placement={PLACEMENT.top} overrides={{
                          Root: {
                            style: {
                              zIndex: 2
                            }
                          }
                        }} />
                        </TaskLevelQuestionnaireProvider>
                      </EventJournalConsumerInitializer>
                    </PersistenceOrchestratorInitializer>
                  </BufferedEventJournalerInitializer>
                </StudioStoreInitializer>
              </StudioAtomHydrator>
            </FeatureFlagProvider>
          </APIContextProvider>
        </StorageProvider>
      </TaskStateProvider>
    </>;
});
export default Studio;